import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { Button, Message, Loader } from 'rsuite';

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ amount, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Confirm the payment
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    // Return URL after payment (for redirect-based payment methods)
                    return_url: `${window.location.origin}/payment-complete`,
                },
                redirect: 'if_required'
            });

            if (stripeError) {
                setError(stripeError.message);
                onError(stripeError.message);
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                // Payment successful - pass result to parent component
                onSuccess({ paymentIntent });
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
            onError(err.message || 'An error occurred');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement 
                options={{
                    layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                    }
                }}
            />
            
            {error && (
                <Message type="error" showIcon className="mt-4">
                    {error}
                </Message>
            )}
            
            <Button
                appearance="primary"
                type="submit"
                disabled={!stripe || !elements || isProcessing}
                loading={isProcessing}
                className="mt-4 w-full"
                size="lg"
            >
                {isProcessing ? 'Processing...' : `Pay $${(amount / 100).toFixed(2)}`}
            </Button>
        </form>
    );
};

const StripePayment = ({ clientSecret, amount, onSuccess, onError }) => {
    const options = {
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#0070f3',
            },
        },
    };

    if (!clientSecret) {
        return <Loader center content="Loading payment..." />;
    }

    return (
        <Elements stripe={stripePromise} options={options}>
            <CheckoutForm 
                amount={amount}
                onSuccess={onSuccess}
                onError={onError}
            />
        </Elements>
    );
};

export default StripePayment;